<script lang="ts">
  /**
   * EmailBodyEditor — the ONE editor for email bodies across the app.
   *
   * Used on every surface where the user edits an email body draft: Today
   * (Respond bucket), Prospecting (sequence cards + single-edit), and
   * ClientView (reply composer + chat draft).
   *
   * What this component owns:
   *  1. Markdown → HTML bridge on incoming `value` (via lib/email_markdown).
   *     If the caller hands us markdown-flavored plain text, we render it as
   *     real formatting in the editor so the user sees what the recipient will
   *     see — not raw "**asterisks**".
   *  2. Minimal toolbar (Bold / Italic / Bullet / Numbered) wired to
   *     document.execCommand. Cmd+B/I work natively too — the toolbar exists
   *     so the affordance is DISCOVERABLE to non-power-users.
   *  3. HTML on the way back out via `bind:value`. The send-path backend
   *     auto-detects HTML vs plain via is_html() and routes accordingly, so
   *     callers don't care what format we emit.
   *
   * IMPORTANT FOR FUTURE CONTRIBUTORS:
   *   For any new email-body editing surface, import THIS component. Do NOT
   *   reach for a raw <textarea> or RichTextarea directly. Everything that
   *   changes about how we edit email bodies (markdown rules, toolbar
   *   buttons, paste behavior, future swap to a richer editor library) is
   *   meant to land in this ONE file and propagate everywhere automatically.
   */
  import RichTextarea from './RichTextarea.svelte';
  import { plainToHtml, isHtml } from './email_markdown';

  let {
    value = $bindable(''),
    placeholder = '',
    rows = 10,
    disabled = false,
  }: {
    value: string;
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
  } = $props();

  // Normalize once on every external `value` change: if the parent hands us
  // markdown-flavored plain text, render it as HTML before binding to
  // RichTextarea. `lastWritten` tracks the value we most recently propagated
  // up, so we can distinguish an external set (regeneration) from our own
  // upstream write (don't clobber the editor on every keystroke).
  let inner = $state(isHtml(value) ? value : plainToHtml(value));
  let lastWritten = $state(value);

  $effect(() => {
    if (value !== lastWritten) {
      // External change — re-normalize
      inner = isHtml(value) ? value : plainToHtml(value);
      lastWritten = value;
    }
  });

  $effect(() => {
    if (inner !== lastWritten) {
      // User edit — propagate up as HTML
      lastWritten = inner;
      value = inner;
    }
  });

  // Toolbar buttons use `onmousedown` + `preventDefault()` so the editor
  // keeps focus during the click — execCommand applies to the live selection
  // instead of failing because the editor lost focus to the button.
  function exec(cmd: string) {
    if (disabled) return;
    document.execCommand(cmd, false);
  }
</script>

<div class="ebe-wrap" class:disabled>
  <div class="ebe-toolbar" role="toolbar" aria-label="Email body formatting">
    <button type="button" class="ebe-btn" title="Bold (Cmd+B)"
            onmousedown={(e) => { e.preventDefault(); exec('bold'); }}>
      <b>B</b>
    </button>
    <button type="button" class="ebe-btn" title="Italic (Cmd+I)"
            onmousedown={(e) => { e.preventDefault(); exec('italic'); }}>
      <i>I</i>
    </button>
    <span class="ebe-sep" aria-hidden="true"></span>
    <button type="button" class="ebe-btn" title="Bullet list"
            onmousedown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>
      • List
    </button>
    <button type="button" class="ebe-btn" title="Numbered list"
            onmousedown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>
      1. List
    </button>
  </div>
  <RichTextarea bind:value={inner} {placeholder} {rows} {disabled} />
</div>

<style>
  .ebe-wrap.disabled { opacity: 0.5; pointer-events: none; }
  .ebe-toolbar {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 6px;
    background: var(--bg-surface, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-bottom: none;
    border-radius: var(--radius-sm, 6px) var(--radius-sm, 6px) 0 0;
  }
  .ebe-btn {
    border: none;
    background: transparent;
    color: var(--text-muted, #64748b);
    padding: 3px 8px;
    font-size: 12px;
    cursor: pointer;
    border-radius: 4px;
    min-width: 24px;
    text-align: center;
    line-height: 1.2;
  }
  .ebe-btn:hover {
    background: var(--bg-hover, rgba(0,0,0,0.04));
    color: var(--text, #1e293b);
  }
  .ebe-sep {
    width: 1px;
    height: 14px;
    background: var(--border, #e2e8f0);
    margin: 0 4px;
  }
  /* Editor sits flush with the toolbar — round only the bottom corners. */
  .ebe-wrap :global(.rich-textarea) {
    border-radius: 0 0 var(--radius-sm, 6px) var(--radius-sm, 6px);
  }
</style>
